import React from 'react';
import { Document, Font, Image, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { fCurrency, fCurrencyWithoutSymbol } from '../../../../../utils/formatNumber';
import moment from 'moment/moment';
import { padNumber } from '../../../../../utils/common';

Font.register({
  family: 'Oswald',
  src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
});
Font.register({
  family: 'arial',
  fonts: [{ src: 'https://ik.imagekit.io/bubblemania/arial.ttf' }],
});
Font.register({
  family: 'sfPro',
  fonts: [{ src: 'https://ik.imagekit.io/bubblemania/SFPRODISPLAYBOLD.ttf' }],
});
Font.register({
  family: 'sfProRegular',
  fonts: [{ src: 'https://ik.imagekit.io/bubblemania/SFPRODISPLAYREGULAR.ttf' }],
});

const styles = StyleSheet.create({
  page: { flexDirection: 'column', width: '80mm' },
  section: { flexGrow: 1 },
  image: { width: '40mm', height: '29.705mm', marginTop: '1mm', objectFit: 'cover', marginLeft: '15mm' },
  headingName: { textAlign: 'center', fontWeight: 600, fontSize: 20 },
  locationName: { textAlign: 'center', fontWeight: 300, fontSize: 14 },
  textCenter: { textAlign: 'center' },
  textRight: { textAlign: 'right' },
  textLeft: { textAlign: 'left' },
  sfPro: { fontFamily: 'sfPro' },
  sfProRegular: { fontFamily: 'sfProRegular' },
  arial: { fontFamily: 'arial' },
  bold: { fontWeight: 'bold' },
  divider: { marginTop: 7, marginBottom: 7, borderBottomWidth: 1, borderBottomColor: '#000', borderBottomStyle: 'solid' },
  dashedDivider: { marginTop: 5, marginBottom: 5, borderBottomWidth: 1, borderBottomColor: '#000', borderBottomStyle: 'dashed' },
  orderNumberTopic: { fontFamily: 'sfPro', fontSize: 9, fontWeight: 'ultrabold', borderStyle: 'solid', textAlign: 'center' },
  orderNumber: { fontFamily: 'sfPro', fontWeight: 'ultrabold', borderStyle: 'solid', textAlign: 'center' },
  twoSides: { flexDirection: 'row' },
  twoSidesCell: { margin: 'auto', marginTop: 1, marginBottom: 1, fontSize: 8, padding: 1, fontFamily: 'arial', fontWeight: 500 },
  tableRow: { flexDirection: 'row', borderStyle: 'solid' },
  tableRowHeader: { flexDirection: 'row', color: '#FFF', borderTopWidth: 0, borderRadius: '4px', backgroundColor: '#000' },
  tableHeaderCell: { margin: 'auto', marginTop: 1, marginBottom: 1, fontSize: 8, padding: 2, fontFamily: 'arial', fontWeight: 700 },
  tableCell: { margin: 'auto', marginTop: 1, marginBottom: 1, fontSize: 8, padding: 2, fontFamily: 'arial', fontWeight: 700 },
  summeryRow: { flexDirection: 'row', borderRadius: '4px' },
  summeryData: { margin: 'auto', marginTop: '1px', marginBottom: '1px', fontSize: 9, padding: 3, fontFamily: 'arial', fontWeight: 500 },
  developedByBox: { padding: 5, backgroundColor: '#000', marginTop: 3, marginBottom: 7, borderRadius: '4px' },
});

const tableStyles = StyleSheet.create({
  details: { ...styles.tableCell, width: '50%' },
  qty: { ...styles.tableCell, width: '10%' },
  price: { ...styles.tableCell, width: '20%' },
  amount: { ...styles.tableCell, width: '20%' },
});
const tableHeaderStyles = StyleSheet.create({
  details: { ...styles.tableHeaderCell, width: '50%' },
  qty: { ...styles.tableHeaderCell, width: '10%' },
  price: { ...styles.tableHeaderCell, width: '20%' },
  amount: { ...styles.tableHeaderCell, width: '20%' },
});
const twoSideStyles = StyleSheet.create({
  details: { ...styles.twoSidesCell, width: '50%', textAlign: 'left' },
  price: { ...styles.twoSidesCell, width: '50%', textAlign: 'right' },
});

// Bill padding helpers
const MAX_BILL_PADDING_MM = 20;

const getPaddingMm = (value, fallbackValue) => {
  if (value === undefined || value === null) {
    return `${fallbackValue}mm`;
  }

  const normalizedValue = typeof value === 'string' ? value.trim().replace(/mm$/i, '') : value;

  if (normalizedValue === '') {
    return `${fallbackValue}mm`;
  }

  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) {
    return `${fallbackValue}mm`;
  }

  return `${Math.min(Math.max(parsedValue, 0), MAX_BILL_PADDING_MM)}mm`;
};

const getBillPaddingStyle = (data) => {
  const defaultLeftPadding = data?.selectedPrinter === 'epson' ? 10 : 0;
  const defaultRightPadding = data?.selectedPrinter === 'epson' ? 0 : 5;

  return {
    paddingLeft: getPaddingMm(data?.billPaddingLeftMm, defaultLeftPadding),
    paddingRight: getPaddingMm(data?.billPaddingRightMm, defaultRightPadding),
  };
};

// Summary row styles
const summaryLineStyles = StyleSheet.create({
  row: { flexDirection: 'row', paddingHorizontal: 3 },
  label: { width: '55%', textAlign: 'left', fontSize: 8, fontFamily: 'arial', fontWeight: 500, paddingVertical: 1.5 },
  value: { width: '45%', textAlign: 'right', fontSize: 8, fontFamily: 'arial', fontWeight: 500, paddingVertical: 1.5 },
  labelBold: { width: '55%', textAlign: 'left', fontSize: 9, fontFamily: 'sfPro', fontWeight: 700, paddingVertical: 2 },
  valueBold: { width: '45%', textAlign: 'right', fontSize: 9, fontFamily: 'sfPro', fontWeight: 700, paddingVertical: 2 },
  labelIndent: { width: '55%', textAlign: 'left', fontSize: 8, fontFamily: 'arial', fontWeight: 500, paddingVertical: 1.5, paddingLeft: 10 },
  sectionTitle: { fontSize: 8, fontFamily: 'sfPro', fontWeight: 700, paddingVertical: 2, paddingHorizontal: 3 },
});

// ─── Sub-components ───────────────────────────────────────────────────────────

function TableHeader() {
  return (
    <View style={styles.tableRowHeader}>
      <Text style={[tableHeaderStyles.details, styles.textLeft, styles.bold]}>Product</Text>
      <Text style={[tableHeaderStyles.qty, styles.textCenter, styles.bold]}>Qty</Text>
      <Text style={[tableHeaderStyles.price, styles.textCenter, styles.bold]}>Price</Text>
      <Text style={[tableHeaderStyles.amount, styles.textCenter, styles.bold]}>Amount</Text>
    </View>
  );
}

function TableRow({ item }) {
  return (
    <View style={styles.tableRow}>
      <Text style={[tableStyles.details, styles.textLeft]}>{item.title}</Text>
      <Text style={[tableStyles.qty, styles.textLeft]}>{item.qty}</Text>
      <Text style={[tableStyles.price, styles.textLeft]}>{item?.price !== undefined ? fCurrencyWithoutSymbol(item.price) : item.value}</Text>
      <Text style={[tableStyles.amount, styles.textRight]}>{item?.amount !== undefined ? fCurrencyWithoutSymbol(item.amount) : item.value}</Text>
    </View>
  );
}

function SingleLineData({ item }) {
  return (
    <View style={styles.twoSides}>
      <Text style={[twoSideStyles.details, styles.textLeft]}>{item.title}</Text>
      <Text style={twoSideStyles.price}>{item?.price ? fCurrency(item?.price || 0) : item?.price === 0 ? fCurrency(0.0) : item.value}</Text>
    </View>
  );
}

/** Simple label–value row */
function SummaryLine({ label, value, bold = false, indent = false }) {
  return (
    <View style={summaryLineStyles.row}>
      <Text style={bold ? summaryLineStyles.labelBold : indent ? summaryLineStyles.labelIndent : summaryLineStyles.label}>{label}</Text>
      <Text style={bold ? summaryLineStyles.valueBold : summaryLineStyles.value}>{value}</Text>
    </View>
  );
}

/** Highlighted grand-total row (black background, white text) */
function GrandTotalRow({ label, value }) {
  return (
    <View style={[summaryLineStyles.row, { backgroundColor: '#000', borderRadius: 4, paddingVertical: 3, marginVertical: 2 }]}>
      <Text style={[summaryLineStyles.labelBold, { color: '#fff' }]}>{label}</Text>
      <Text style={[summaryLineStyles.valueBold, { color: '#fff' }]}>{value}</Text>
    </View>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function PosInvoicePdfV2({ data }) {
  // console.log(data);

  // Compute subtotal from invoice items
  const subtotal = (data?.invoiceItems || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  // Separate discount-type payments (isIncrement === false) from real payments (isIncrement === true / normal)
  const discountPayments = (data?.payments || []).filter((p) => p.code == 'DISCOUNT' && p.amount > 0);
  const regularPayments = (data?.payments || []).filter((p) => p.code != 'DISCOUNT' && p.amount > 0);

  // console.log(discountPayments);
  // console.log('regularPayments', regularPayments);

  const totalDiscount = discountPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const totalPaid = (regularPayments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const grandTotal = data?.grandTotal - totalDiscount;
  // console.log('grandTotal', grandTotal);
  // console.log('data?.grandTotal', data?.grandTotal);
  // console.log('subtotal', subtotal);
  // console.log('totalDiscount', totalDiscount);
  
  const balanceOrChange = data?.balanceAmount ?? 0;
  const billPaddingStyle = getBillPaddingStyle(data);

  return (
    <Document>
      <Page style={styles.page} size={[226.772, 'auto']}>
        <View style={{ ...styles.section, ...billPaddingStyle }}>
          {/* ── Header ─────────────────────────────────────────────────────── */}
          {!data?.isKOT && <Image style={styles.image} src={'https://ik.imagekit.io/bubblemania/output-onlinepngtools%20(2).png'} />}
          <Text style={[styles.sfPro, styles.headingName]}>
            {data?.isKOT ? `KOT` : `Bubble Mania`} {data?.isReprint ? ' - RePrint' : ''}
          </Text>
          {!data?.isKOT && (
            <>
              <Text style={[styles.sfProRegular, styles.locationName]}>{data?.locationName}</Text>
              <Text style={[styles.sfProRegular, { fontSize: 8 }, styles.textCenter]}>{data?.locationAddress}</Text>
              <Text style={[styles.sfProRegular, { fontSize: 8 }, styles.textCenter]}>Phone: {data?.phoneNumber}</Text>
            </>
          )}

          {/* ── Order Number ───────────────────────────────────────────────── */}
          <View style={[{ margin: '2mm 6mm 2mm 6mm', padding: 5, border: '2px solid #000', borderRadius: '10px' }]}>
            <Text style={[styles.orderNumberTopic]}>Order Number</Text>
            <Text style={[styles.orderNumber]}>{padNumber(data?.orderNumber, 6)}</Text>
          </View>

          {/* ── Order Metadata ─────────────────────────────────────────────── */}
          <SingleLineData item={{ title: 'Order Type', value: data?.orderType }} />
          <SingleLineData item={{ title: 'Cashier Name', value: data?.cashierName }} />
          <SingleLineData item={{ title: 'Cashier Drawer ID', value: data?.cashierDrawerId }} />
          <SingleLineData item={{ title: 'Customer Name', value: data?.customerName }} />
          {data?.loyaltyPoint && <SingleLineData item={{ title: 'Customer Loyalty Point', price: data?.loyaltyPoint }} />}
          <SingleLineData item={{ title: 'Date & Time', value: moment(data?.dateTime).format('HH:mm:ss DD MMMM YYYY') }} />
          {data?.tableNumber && <SingleLineData item={{ title: 'Table Number', value: data?.tableNumber }} />}
          {data?.specialNote && <SingleLineData item={{ title: 'Special Note', value: data?.specialNote }} />}

          {/* ── Invoice Items Table ────────────────────────────────────────── */}
          <View style={styles.divider} />
          <View>
            <TableHeader />
            {data?.invoiceItems?.map((item, index) => (
              <TableRow key={index} item={item} />
            ))}
          </View>

          {/* ── Totals Section ─────────────────────────────────────────────── */}
          <View style={styles.dashedDivider} />

          <SummaryLine label='Subtotal:' value={fCurrencyWithoutSymbol(subtotal)} />

          {discountPayments.length > 0 && (
            <>
              {discountPayments.map((p, i) => (
                <SummaryLine key={`disc-${i}`} label={`- ${p.name}:`} value={`-${fCurrencyWithoutSymbol(p.amount)}`} />
              ))}
            </>
          )}

          {data?.totalTax > 0 && <SummaryLine label='+ Tax:' value={`+${fCurrencyWithoutSymbol(data.totalTax)}`} />}

          <View style={{ marginTop: 3 }} />
          <GrandTotalRow label='Grand Total:' value={fCurrencyWithoutSymbol(grandTotal)} />

          {/* ── Payments Section ───────────────────────────────────────────── */}
          <View style={styles.dashedDivider} />

          <Text style={summaryLineStyles.sectionTitle}>Payments:</Text>
          {regularPayments.map((p, i) => (
            <SummaryLine key={`pay-${i}`} indent label={`${p.name}:`} value={fCurrencyWithoutSymbol(p.amount)} />
          ))}
          {/* {discountPayments.map((p, i) => (
            <SummaryLine key={`dpay-${i}`} indent label={`${p.name}:`} value={fCurrencyWithoutSymbol(p.amount)} />
          ))} */}

          <View style={styles.dashedDivider} />
          <SummaryLine label='Total Paid:' value={fCurrencyWithoutSymbol(totalPaid)} bold />
          <View style={{ marginTop: 2 }} />
          <SummaryLine label='Balance / Change:' value={fCurrencyWithoutSymbol(balanceOrChange)} bold />

          {/* ── Item Count ─────────────────────────────────────────────────── */}
          <View style={styles.dashedDivider} />
          <SummaryLine label='Number Of Items:' value={data?.totalItemCount || 0} />

          {/* ── Footer ─────────────────────────────────────────────────────── */}
          {!data?.isKOT && (
            <>
              <View style={styles.divider} />
              <Text style={[styles.arial, { fontSize: 8, fontWeight: 600, marginLeft: '5mm', marginRight: '5mm' }, styles.textCenter]}>We're thankful for your visit.</Text>
              <Text style={[styles.arial, { fontSize: 8, fontWeight: 600, marginLeft: '5mm', marginRight: '5mm' }, styles.textCenter]}>Come again for another delicious delights.</Text>
              <Text style={[styles.arial, { fontSize: 8, fontWeight: 600, marginLeft: '5mm', marginRight: '5mm' }, styles.textCenter]}>Have a wonderful day!</Text>
              <View style={styles.divider} />
              <View style={styles.developedByBox}>
                <Text style={[styles.arial, { fontSize: 7, color: '#FFF' }, styles.textCenter]}>Developed By www.expergen.com</Text>
              </View>
            </>
          )}
        </View>
      </Page>
    </Document>
  );
}

export default PosInvoicePdfV2;
